/**
 * ErrorBoundary — class component that catches render errors in its subtree.
 *
 * Design: ADR-3 / platform-hardening §ErrorBoundary / design §3
 *
 * Requirements:
 *   - A crash in one panel MUST NOT blank the app.
 *   - Fallback MUST be accessible: role="alert", aria-live="assertive",
 *     keyboard-focusable, no raw stack trace exposed to the user.
 *   - "Reintentar" button must meet WCAG 2.1 AA touch target (min-h-[44px]).
 *   - Details go to console.error only — never to the DOM.
 *
 * Placement (see main.tsx / AdminPanel.tsx / App.tsx):
 *   - Root: wraps <App> in main.tsx (app never blanks).
 *   - Per-panel: wraps each admin panel tab and TrackingContainer/ChatPanel.
 */

import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

export interface ErrorBoundaryProps {
  /** Custom fallback UI. When omitted a default accessible fallback is rendered. */
  fallback?: ReactNode;
  /** Called when the user clicks "Reintentar" or "Recargar". */
  onReset?: () => void;
  /** Content to protect. */
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  /** Error message (safe summary — never a stack trace exposed to DOM). */
  errorMessage: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    // Extract a safe summary (no stack trace / PII in DOM).
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    return { hasError: true, errorMessage };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Log full details to console only — never to DOM or error responses.
    console.error("[ErrorBoundary] Caught error:", error, info.componentStack);
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, errorMessage: "" });
    this.props.onReset?.();
  };

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Custom fallback takes precedence.
    if (this.props.fallback != null) {
      return this.props.fallback;
    }

    // Default accessible fallback.
    return (
      <div
        role="alert"
        aria-live="assertive"
        className="flex flex-col items-center gap-3 rounded-lg border border-red-900/50 bg-red-950/20 px-4 py-8 text-center"
      >
        <p className="text-sm font-medium text-red-400">
          Algo salió mal en este panel.
        </p>
        <p className="text-xs text-zinc-500">
          El resto de la aplicación sigue funcionando.
        </p>
        <button
          type="button"
          onClick={this.handleReset}
          className="min-h-[44px] rounded-lg border border-zinc-700 px-4 text-sm font-medium text-zinc-300 transition-colors hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          Reintentar
        </button>
      </div>
    );
  }
}
